import { useState } from "react";
import { Card } from "../../../../../ui/card";
import { Badge } from "../../../../../ui/badge";
import { Button } from "../../../../../ui/button";
import { Progress } from "../../../../../ui/progress";
import { CheckCircle, Star } from "lucide-react";
import { supabase } from "../../../../../../integrations/supabase/client";
import { useToast } from "../../../../../../hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface Review {
  id: string;
  ticket_id: string;
  author_name: string;
  comment_text: string;
  rating: number;
  platform: string;
  posted_at: string;
  status: string;
  urgency: string;
  assigned_to?: string;
  response_time?: number;
  recovery_progress: number;
  created_at: string;
}

interface TicketCardProps {
  review: Review & { aiReply?: string };
  onUpdate: () => void;
  onGenerateReply: (reviewId: string, reviewText: string, rating: number, platform: string, authorName: string) => void;
  onCopyReply: (reply: string) => void;
  isGeneratingReply: boolean;
}

export const TicketCard = ({ review, onUpdate, onGenerateReply, onCopyReply, isGeneratingReply }: TicketCardProps) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "high":
        return "bg-destructive text-destructive-foreground";
      case "medium":
        return "bg-orange-500 text-white";
      default:
        return "bg-yellow-500 text-white";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Resolved":
        return "bg-green-500 text-white";
      case "In Progress":
        return "bg-blue-500 text-white";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const handleMarkResolved = async () => {
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("reviews")
        .update({
          status: "Resolved",
          recovery_progress: 100,
          resolution_date: new Date().toISOString(),
        })
        .eq("id", review.id);

      if (error) throw error;

      toast({
        title: "Ticket resolved",
        description: `${review.ticket_id} has been marked as resolved.`,
      });

      onUpdate();
    } catch (error) {
      console.error("Error resolving ticket:", error);
      toast({
        title: "Error",
        description: "Failed to resolve ticket",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRevertResolved = async () => {
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("reviews")
        .update({
          status: "Pending",
          resolution_date: null,
        })
        .eq("id", review.id);

      if (error) throw error;

      toast({
        title: "Ticket reopened",
        description: `${review.ticket_id} has been moved back to pending.`,
      });

      onUpdate();
    } catch (error) {
      console.error("Error reverting ticket:", error);
      toast({
        title: "Error",
        description: "Failed to revert ticket",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };


  return (
    <Card  className={`p-6 hover:shadow-lg transition-shadow border-l-4 ${review.urgency === "high"?'high-urgent':'low-urgent'}`}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg">{review.author_name || "Anonymous"}</h3>
          {review.urgency && (
            <Badge className={getUrgencyColor(review.urgency)}>
              {review.urgency}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={getStatusColor(review.status)}>{review.status}</Badge>
          <span className="text-xs text-muted-foreground">{review.ticket_id}</span>
        </div>
      </div>

      {/* Info Row */}
      <div className="flex flex-wrap gap-4 mb-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          {Array.from({ length: review.rating }).map((_, i) => (
            <Star key={i} className="h-3 w-3 fill-yellow-500 text-yellow-500" />
          ))}
          <span className="ml-1">{review.rating} stars</span>
        </span>
        <span>Platform: {review.platform}</span>
        <span>{formatDistanceToNow(new Date(review.posted_at || review.created_at), { addSuffix: true })}</span>
        {review.response_time && (
          <span className={review.response_time > 30 ? "text-destructive" : "text-green-500"}>
            Response: {review.response_time}m
          </span>
        )}
      </div>

      {/* Comment */}
      <p className="text-sm mb-4">{review.comment_text}</p>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-muted-foreground">Recovery Progress</span>
          <span className="text-xs font-medium">{review.recovery_progress}%</span>
        </div>
        <Progress value={review.recovery_progress} className="h-2" />
      </div>

      {/* AI Reply Section */}
      {review.aiReply && (
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">AI Generated Reply:</p>
              <p className="text-sm text-muted-foreground">{review.aiReply}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopyReply(review.aiReply!)}
            >
              Copy
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onGenerateReply(review.id, review.comment_text, review.rating, review.platform, review.author_name)}
          disabled={isGeneratingReply}
          variant="outline"
          size="sm"
        >
          {isGeneratingReply ? "Generating..." : "Generate AI Reply"}
        </Button>
        {review.status !== "Resolved" ? (
          <Button
            size="sm"
            className="gap-1 ml-auto"
            onClick={handleMarkResolved}
            disabled={isUpdating}
          >
            <CheckCircle className="h-3 w-3" />
            {isUpdating ? "Updating..." : "Mark Resolved"}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="gap-1 ml-auto"
            onClick={handleRevertResolved}
            disabled={isUpdating}
          >
            {isUpdating ? "Reverting..." : "Reopen Ticket"}
          </Button>
        )}
      </div>
    </Card>
  );
};
